import { ISOCountryCode, localizedCountryName } from '@pmi.web/countries';
import {
  AutocompleteInput,
  DropdownInput,
  TextInput,
} from '@pmi.web/react-inputs';
import { get, has } from 'lodash-es';
import { ChangeEvent, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUSAddressSuggestions } from './useUSAddressSuggestions';
import { useZipSuggestions } from './useZipSuggestions';
import { IAddress } from '../../../../types/IAddress';

export function USCountryAddress({
  env,
  isoCountryCode,
  required,
}: {
  readonly required: boolean;
  readonly isoCountryCode: ISOCountryCode;
  readonly env: 'development' | 'production';
}) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const address: IAddress = watch('address');
  const isZipApplied = !!address?.zip;

  const { zipSuggestions, fetchZipSuggestions } = useZipSuggestions(
    env,
    isoCountryCode,
  );
  const { addressSuggestions, fetchAddressSuggestions } =
    useUSAddressSuggestions(
      address?.zip ?? '',
      address?.locality ?? '',
      address?.administrativeDistrictLevel1 ?? '',
      env,
    );

  const autoCompleteOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const zip = event?.target.value;
      if (address.locality || address.administrativeDistrictLevel1) {
        setValue('address.addressLine1', '');
        setValue('address.addressLine2', '');
        setValue('address.locality', '');
        setValue('address.administrativeDistrictLevel1', '');
        setValue('address.administrativeDistrictLevel2', '');
        setValue('address.administrativeDistrictLevel3', '');
      }
      if (zip?.length >= 2) {
        fetchZipSuggestions(zip);
      }
    },
    [address, setValue, fetchZipSuggestions],
  );

  return (
    <>
      <AutocompleteInput
        type="number"
        required={required}
        label={t('Zip')}
        {...register('address.zip', {
          required: required ? t('This field is required.') : false,
          onChange: autoCompleteOnChange,
        })}
        suggestions={zipSuggestions}
        onSuggestionClick={s => {
          const suggestion = s as IAddress;
          setValue('address.addressLine1', suggestion.addressLine1);
          setValue('address.addressLine2', suggestion.addressLine2);
          setValue('address.zip', suggestion.zip);
          setValue('address.locality', suggestion.locality);
          setValue('address.country', suggestion.country);
          setValue(
            'address.administrativeDistrictLevel1',
            suggestion.administrativeDistrictLevel1,
          );
          setValue(
            'address.administrativeDistrictLevel2',
            suggestion.administrativeDistrictLevel2,
          );
          setValue(
            'address.administrativeDistrictLevel3',
            suggestion.administrativeDistrictLevel3,
          );
          fetchZipSuggestions((s as IAddress).zip ?? '');
        }}
        errors={
          has(errors, 'address.zip')
            ? [get(errors, 'address.zip')?.message?.toString() ?? '']
            : []
        }
      />

      <TextInput
        label={t('State')}
        required={required}
        disabled={true}
        {...register('address.administrativeDistrictLevel1', {
          required: required ? t('This field is required.') : false,
        })}
        errors={
          has(errors, 'address.administrativeDistrictLevel1')
            ? [
                get(
                  errors,
                  'address.administrativeDistrictLevel1',
                )?.message?.toString() ?? '',
              ]
            : []
        }
      />

      <TextInput
        label={t('County')}
        required={required}
        disabled={true}
        {...register('address.administrativeDistrictLevel2', {
          required: required ? t('This field is required.') : false,
        })}
        errors={
          has(errors, 'address.administrativeDistrictLevel2')
            ? [
                get(
                  errors,
                  'address.administrativeDistrictLevel2',
                )?.message?.toString() ?? '',
              ]
            : []
        }
      />

      <TextInput
        label={t('City')}
        required={required}
        disabled={true}
        {...register('address.locality', {
          required: required ? t('This field is required.') : false,
        })}
        errors={
          has(errors, 'address.locality')
            ? [get(errors, 'address.locality')?.message?.toString() ?? '']
            : []
        }
      />

      <AutocompleteInput
        type="text"
        required={required}
        disabled={!isZipApplied}
        label={t('Address')}
        {...register('address.addressLine1', {
          required: required ? t('This field is required.') : false,
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            const addressLine1 = event?.target.value;
            if (addressLine1 && addressLine1.length >= 5) {
              fetchAddressSuggestions(addressLine1);
            }
          },
        })}
        suggestions={addressSuggestions}
        onSuggestionClick={s => {
          const suggestion = s as IAddress;
          setValue('address.addressLine1', suggestion.addressLine1);
          fetchAddressSuggestions((s as IAddress).addressLine1 ?? '');
        }}
        errors={
          has(errors, 'address.addressLine1')
            ? [get(errors, 'address.addressLine1')?.message?.toString() ?? '']
            : []
        }
      />

      <DropdownInput
        label={t('Country')}
        disabled
        {...register('address.country')}
        options={[
          {
            label: localizedCountryName(isoCountryCode, language),
            value: isoCountryCode,
          },
        ]}
      />
    </>
  );
}
